<template>
  <div class="cfg-tab">
    <div class="cfg-intro">
      <v-icon>mdi-information-outline</v-icon>
      <div>
        <p>
          Cauzele din „Demers nou” (Tip cerere), în ordinea din listă. O cauză apare doar la tipurile de echipament
          pentru care este oferită; mutarea între tipuri se aplică doar demersurilor noi.
        </p>
        <p>Demersurile depuse își păstrează cauza cu denumirea de atunci.</p>
      </div>
    </div>

    <div class="cfg-chips" role="radiogroup" aria-label="Filtru după tipul de echipament">
      <button
        v-for="f in filters"
        :key="f.key"
        type="button"
        role="radio"
        class="cfg-chip"
        :class="{ 'is-active': filter === f.key }"
        :aria-checked="filter === f.key"
        @click="filter = f.key"
      >
        <span class="cfg-chip__name">{{ f.label }}</span><span class="cfg-chip__count">{{ f.count }}</span>
      </button>
    </div>

    <div class="cfg-toolbar">
      <h3 class="panel-title">{{ filterLabel }} <span class="count">{{ filtered.length }}</span></h3>
      <span class="grow-spacer" />
      <v-text-field
        v-model="search"
        class="cfg-search"
        prepend-inner-icon="mdi-magnify"
        placeholder="Caută o cauză"
        aria-label="Caută o cauză"
        outlined
        dense
        single-line
        hide-details
        clearable
      ></v-text-field>
      <v-btn class="btn-green" @click="openCreate"><v-icon left>mdi-plus</v-icon>Cauză nouă</v-btn>
    </div>

    <v-progress-linear v-if="loading && !loaded" indeterminate color="primary" class="mb-2" />
    <div class="cfg-list">
      <div v-if="loaded && !filtered.length" class="cfg-empty">Nicio cauză.</div>
      <div v-for="(c, i) in filtered" :key="c.id" class="cfg-row" :class="{ 'is-hidden': c.is_hidden }">
        <div class="cfg-order">
          <v-btn icon small :disabled="i === 0 || saving" aria-label="Mută mai sus" @click="move(c, -1)">
            <v-icon>mdi-chevron-up</v-icon>
          </v-btn>
          <v-btn icon small :disabled="i === filtered.length - 1 || saving" aria-label="Mută mai jos" @click="move(c, 1)">
            <v-icon>mdi-chevron-down</v-icon>
          </v-btn>
        </div>
        <div class="cfg-row__main">
          <div class="cfg-row__title">
            <span class="cfg-row__name">{{ c.type }}</span>
            <span v-if="c.creates_equipment" class="cfg-tag cfg-tag--green">
              <v-icon>mdi-package-variant-plus</v-icon>Creează echipament nou
            </span>
            <span v-if="c.is_hidden" class="cfg-tag"><v-icon>mdi-eye-off-outline</v-icon>Ascunsă</span>
            <span v-if="c.circuit_id" class="cfg-tag cfg-tag--plum" :title="circuitPath(c.circuit_id)">
              <v-icon>mdi-sitemap-outline</v-icon>Circuit propriu: {{ circuitName(c.circuit_id) }}
            </span>
          </div>
          <div class="cfg-row__meta">
            <span class="causes__for">Pentru:</span>
            <span
              v-for="id in c.equipment_type_ids"
              :key="id"
              class="cfg-tag"
              :class="typeHidden(id) ? '' : 'cfg-tag--sky'"
            >{{ typeName(id) }}</span>
            <span v-if="!c.equipment_type_ids.length" class="cfg-tag cfg-tag--amber">niciun tip, nu apare în „Demers nou”</span>
          </div>
          <div v-if="notOffered(c).length" class="cfg-row__sub causes__warn">
            Nu apare la {{ notOffered(c).join(", ") }}: tipul nu permite cereri de echipament nou.
          </div>
          <div class="cfg-usage">{{ usageText(c.usage) || "Nefolosită încă" }}</div>
        </div>
        <div class="cfg-row__actions">
          <v-btn icon color="primary" aria-label="Editează cauza" @click="openEdit(c)">
            <v-icon>mdi-pencil-outline</v-icon>
          </v-btn>
          <v-btn
            icon
            :aria-label="c.is_hidden ? 'Arată cauza' : 'Ascunde cauza'"
            :title="c.is_hidden ? 'Arată din nou' : 'Ascunde'"
            :disabled="saving"
            @click="toggleHidden(c)"
          >
            <v-icon>{{ c.is_hidden ? "mdi-eye-outline" : "mdi-eye-off-outline" }}</v-icon>
          </v-btn>
          <v-btn icon color="error" aria-label="Șterge cauza" @click="remove(c)">
            <v-icon>mdi-delete-outline</v-icon>
          </v-btn>
        </div>
      </div>
    </div>

    <v-dialog v-model="dialog" persistent scrollable max-width="720" :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card>
        <v-form class="fdlg__form" @submit.prevent="save">
          <div class="fdlg__head">
            <div>
              <h2 class="fdlg__title">{{ editing ? "Editează cauza" : "Cauză nouă" }}</h2>
              <p class="fdlg__sub">{{ editing ? editing.type : "Apare în „Demers nou” la tipurile alese." }}</p>
            </div>
            <v-btn icon aria-label="Închide" @click="dialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>

          <v-card-text ref="body" class="fdlg__body">
            <div v-if="editing && editing.used" class="cfg-used-note">
              <v-icon small>mdi-alert-outline</v-icon>
              <p>În uz: {{ usageText(editing.usage) }}. Demersurile depuse păstrează denumirea cu care au fost depuse.</p>
            </div>

            <section class="form-section">
              <div class="form-section__label"><v-icon small>mdi-form-textbox</v-icon>Denumire</div>
              <v-text-field
                v-model="form.type"
                label="Denumirea cauzei*"
                prepend-inner-icon="mdi-format-list-bulleted-type"
                outlined
                dense
                maxlength="250"
                :error-messages="nameError"
                @input="nameError = ''"
              ></v-text-field>
              <v-switch
                v-model="form.creates_equipment"
                inset
                color="primary"
                class="mt-0 mb-2"
                label="Creează echipament nou"
                hint="Demersul cere un echipament nou (Procurare, Eliberare de la depozit), care se creează odată cu el. Fără bifă, cauza se alege pentru un echipament existent."
                persistent-hint
              ></v-switch>
            </section>

            <section class="form-section">
              <div class="form-section__label"><v-icon small>mdi-shape-outline</v-icon>Oferită pentru tipurile</div>
              <p class="form-section__note">
                Se aplică doar demersurilor noi. O cauză „Creează echipament nou” apare doar la tipurile la care
                utilizatorii pot cere echipament nou.
              </p>
              <v-chip-group v-model="form.equipment_type_ids" multiple column active-class="causes__chip--on" class="mb-2">
                <v-chip
                  v-for="t in typeOptions"
                  :key="t.id"
                  :value="t.id"
                  filter
                  outlined
                  class="causes__chip"
                >
                  {{ t.type }}<span v-if="t.is_hidden" class="causes__chip-note">&nbsp;(ascuns)</span>
                </v-chip>
              </v-chip-group>
              <div v-if="!form.equipment_type_ids.length" class="form-error">
                Fără niciun tip, cauza nu apare în „Demers nou”.
              </div>
            </section>

            <section class="form-section">
              <div class="form-section__label"><v-icon small>mdi-sitemap-outline</v-icon>Circuit de aprobare</div>
              <p class="form-section__note">
                De obicei, cauza urmează circuitul tipului de echipament. Alegeți un circuit propriu dacă această cauză
                se aprobă altfel (de exemplu, doar de Șeful departamentului). Se aplică doar demersurilor noi.
              </p>
              <div class="cfg-choices" role="radiogroup" aria-label="Circuit de aprobare">
                <button
                  type="button"
                  role="radio"
                  class="cfg-choice"
                  :class="{ 'is-active': form.circuit_id === null }"
                  :aria-checked="String(form.circuit_id === null)"
                  @click="form.circuit_id = null"
                >
                  <v-icon>{{ form.circuit_id === null ? "mdi-radiobox-marked" : "mdi-radiobox-blank" }}</v-icon>
                  <span>
                    <span class="cfg-choice__name">Circuitul tipului de echipament</span>
                    <span class="cfg-choice__hint">{{ typeCircuitsText }}</span>
                  </span>
                </button>
                <button
                  v-for="x in circuitOptions"
                  :key="x.id"
                  type="button"
                  role="radio"
                  class="cfg-choice"
                  :class="{ 'is-active': form.circuit_id === x.id }"
                  :aria-checked="String(form.circuit_id === x.id)"
                  @click="form.circuit_id = x.id"
                >
                  <v-icon>{{ form.circuit_id === x.id ? "mdi-radiobox-marked" : "mdi-radiobox-blank" }}</v-icon>
                  <span>
                    <span class="cfg-choice__name">{{ x.name }}<template v-if="x.is_hidden"> (ascuns)</template></span>
                    <span class="cfg-choice__hint">{{ pathText(x) }}</span>
                  </span>
                </button>
              </div>
            </section>
          </v-card-text>

          <div class="fdlg__foot">
            <v-spacer></v-spacer>
            <v-btn text large @click="dialog = false">Anulare</v-btn>
            <v-btn class="btn-green" large type="submit" :loading="saving">
              <v-icon left>mdi-content-save-outline</v-icon>Salvează
            </v-btn>
          </div>
        </v-form>
      </v-card>
    </v-dialog>

    <ConfirmUsedDialog ref="confirmUsed" />
    <RemoveDialog ref="remove" />
  </div>
</template>

<script>
import * as api from "./api";
import * as helper from "@/helper/helper";
import configMixin from "./configMixin";
import ConfirmUsedDialog from "./ConfirmUsedDialog.vue";
import RemoveDialog from "./RemoveDialog.vue";
import { bySortOrder, cleanText, orderPatches, plain, usageText } from "./format";
import { circuitPathText } from "@/config/circuits";

export default {
  name: "CausesTab",
  components: { ConfirmUsedDialog, RemoveDialog },
  mixins: [configMixin],
  data() {
    return {
      causes: [],
      types: [],
      circuits: [],
      // 'all', 'none' (offered for no type) or an equipment type id
      filter: "all",
      search: "",
      dialog: false,
      editing: null,
      form: { equipment_type_ids: [], circuit_id: null },
      nameError: "",
    };
  },
  computed: {
    sortedTypes() {
      return [...this.types].sort(bySortOrder);
    },
    typeOptions() {
      // hidden types only when the cause already has them (an edit shows the current value)
      const current = this.editing ? this.editing.equipment_type_ids : [];
      return this.sortedTypes.filter((t) => !t.is_hidden || current.includes(t.id));
    },
    sorted() {
      return [...this.causes].sort(bySortOrder);
    },
    // hidden circuits only when the cause already has it
    circuitOptions() {
      const current = this.editing ? this.editing.circuit_id : null;
      return [...this.circuits].sort(bySortOrder).filter((c) => !c.is_hidden || c.id === current);
    },
    // "Imprimanta: Standard; Calculatoare: Calculatoare (cu CAZ)" for the types ticked in the form
    typeCircuitsText() {
      const ids = this.form.equipment_type_ids || [];
      if (!ids.length) return "Alegeți tipurile mai sus.";
      return ids.map((id) => `${this.typeName(id)}: ${this.circuitName((this.typeOf(id) || {}).circuit_id)}`).join("; ");
    },
    filters() {
      const list = [{ key: "all", label: "Toate", count: this.causes.length }];
      this.sortedTypes.forEach((t) => {
        list.push({ key: t.id, label: t.type, count: this.causes.filter((c) => c.equipment_type_ids.includes(t.id)).length });
      });
      const none = this.causes.filter((c) => !c.equipment_type_ids.length).length;
      if (none) list.push({ key: "none", label: "Fără tip", count: none });
      return list;
    },
    filterLabel() {
      if (this.filter === "all") return "Toate cauzele";
      if (this.filter === "none") return "Cauze fără tip";
      return `Cauze pentru ${this.typeName(this.filter)}`;
    },
    filtered() {
      const term = plain(this.search);
      return this.sorted.filter((c) => {
        if (this.filter === "none" && c.equipment_type_ids.length) return false;
        if (typeof this.filter === "number" && !c.equipment_type_ids.includes(this.filter)) return false;
        return !term || plain(c.type).includes(term);
      });
    },
  },
  watch: {
    // a type deleted meanwhile
    filters(list) {
      if (!list.some((f) => f.key === this.filter)) this.filter = "all";
    },
  },
  methods: {
    usageText,
    async load() {
      const [causes, types, circuits] = await Promise.all([api.getCauses(), api.getEquipmentTypes(), api.getCircuits()]);
      this.causes = causes;
      this.types = types;
      this.circuits = circuits;
    },
    circuitOf(id) {
      return this.circuits.find((c) => c.id === id);
    },
    circuitName(id) {
      const c = this.circuitOf(id);
      return c ? c.name : "—";
    },
    pathText(c) {
      const path = circuitPathText(c.steps);
      return c.rector_review ? `${path} (Rectorul poate trimite „Pentru examinare” la Șeful DTI)` : path;
    },
    circuitPath(id) {
      const c = this.circuitOf(id);
      return c ? this.pathText(c) : "";
    },
    typeOf(id) {
      return this.types.find((t) => t.id === id);
    },
    typeName(id) {
      const t = this.typeOf(id);
      return t ? t.type : `#${id}`;
    },
    typeHidden(id) {
      const t = this.typeOf(id);
      return !t || t.is_hidden;
    },
    // a new-item cause is only offered where users may ask for a new item
    notOffered(c) {
      if (!c.creates_equipment) return [];
      return c.equipment_type_ids.filter((id) => this.typeOf(id) && !this.typeOf(id).allows_new_item).map(this.typeName);
    },
    openCreate() {
      this.editing = null;
      this.form = {
        type: "",
        creates_equipment: false,
        equipment_type_ids: typeof this.filter === "number" ? [this.filter] : [],
        circuit_id: null,
      };
      this.openDialog();
    },
    openEdit(c) {
      this.editing = c;
      this.form = {
        type: c.type,
        creates_equipment: !!c.creates_equipment,
        equipment_type_ids: [...c.equipment_type_ids],
        circuit_id: c.circuit_id || null,
      };
      this.openDialog();
    },
    openDialog() {
      this.nameError = "";
      this.dialog = true;
      helper.scrollToTop(this);
    },
    async save() {
      const name = cleanText(this.form.type);
      if (!name) {
        this.nameError = "Denumirea este obligatorie";
        return;
      }
      const ids = [...this.form.equipment_type_ids].sort((a, b) => a - b);
      const c = this.editing;
      if (!c) {
        const created = await this.change(
          () => api.createCause({ type: name, creates_equipment: !!this.form.creates_equipment, equipment_type_ids: ids, circuit_id: this.form.circuit_id }),
          { done: `Cauza „${name}” a fost adăugată`, fallback: "Eroare la adăugarea cauzei" }
        );
        if (created) this.dialog = false;
        return;
      }
      const body = {};
      if (name !== c.type) body.type = name;
      if (!!this.form.creates_equipment !== !!c.creates_equipment) body.creates_equipment = !!this.form.creates_equipment;
      if (ids.join(",") !== [...c.equipment_type_ids].sort((a, b) => a - b).join(",")) body.equipment_type_ids = ids;
      if ((this.form.circuit_id || null) !== (c.circuit_id || null)) body.circuit_id = this.form.circuit_id || null;
      if (!Object.keys(body).length) {
        this.dialog = false;
        return;
      }
      const saved = await this.change((extra) => api.updateCause(c.id, { ...body, ...extra }), {
        done: `Cauza „${name}” a fost salvată`,
        fallback: "Eroare la salvarea cauzei",
        confirm: {
          title: `Redenumiți cauza „${c.type}”?`,
          from: c.type,
          to: name,
          changes: ["Demersurile noi se depun cu denumirea nouă."],
          keeps: ["Demersurile depuse păstrează cauza cu denumirea cu care au fost depuse: sunt documente."],
        },
      });
      if (saved) this.dialog = false;
    },
    toggleHidden(c) {
      const hide = !c.is_hidden;
      return this.change(() => api.updateCause(c.id, { is_hidden: hide }), {
        done: hide ? `Cauza „${c.type}” este ascunsă: nu mai apare în „Demers nou”` : `Cauza „${c.type}” apare din nou în „Demers nou”`,
        fallback: "Eroare la salvarea cauzei",
      });
    },
    move(c, dir) {
      const patches = orderPatches(this.filtered, c, dir);
      if (!patches.length) return;
      this.change(
        async () => {
          for (const p of patches) await api.updateCause(p.id, { sort_order: p.sort_order });
        },
        { done: "Ordinea a fost salvată", fallback: "Eroare la salvarea ordinii" }
      );
    },
    async remove(c) {
      const canDelete = !c.used;
      const choice = await this.$refs.remove.ask({
        title: canDelete ? `Ștergeți cauza „${c.type}”?` : `Cauza „${c.type}” nu poate fi ștearsă`,
        text: canDelete
          ? "Nu apare în niciun demers, deci poate fi ștearsă."
          : `Apare în ${usageText(c.usage)}. ` +
            (c.is_hidden
              ? "Este deja ascunsă, deci nu mai apare în „Demers nou”."
              : "Ascundeți-o: nu va mai apărea în „Demers nou”, iar demersurile existente rămân neschimbate."),
        canDelete,
        canHide: !canDelete && !c.is_hidden,
      });
      if (choice === "delete") {
        await this.change(() => api.deleteCause(c.id), { done: `Cauza „${c.type}” a fost ștearsă`, fallback: "Eroare la ștergerea cauzei" });
      } else if (choice === "hide") {
        await this.toggleHidden(c);
      }
    },
  },
};
</script>

<style scoped>
.causes__for {
  color: var(--ink-3);
  font-size: 0.9rem;
  font-weight: 600;
}
.causes__warn {
  color: var(--amber);
  font-weight: 600;
}
.causes__chip.v-chip {
  font-weight: 600;
}
.causes__chip.causes__chip--on.v-chip {
  background: var(--mint-50) !important;
  border-color: var(--green) !important;
  color: var(--green-700) !important;
}
.causes__chip-note {
  color: var(--ink-3);
  font-weight: 400;
}
</style>
